import { type FC, useContext } from 'react';
import { utils } from 'ethers';
import { useFragment, graphql } from 'react-relay';
import { FormattedMessage } from 'dibs-react-intl';
import HeadingLevel from 'dibs-controlled-heading/exports/HeadingLevel.tsx';
import { Spinner } from 'dibs-elements/exports/Spinner.tsx';
import { ModalBody } from 'dibs-elements/exports/ModalBody.tsx';
import { OrderedList } from 'dibs-elements/exports/OrderedList.tsx';
import { NftPdpBidModalContext } from '../NftPdpBidModalContext.tsx';
import { addParams } from 'dibs-crypto-utils/exports/imageOptimizationParams.ts';

import { type BidPendingContent_nftItem$key } from './__generated__/BidPendingContent_nftItem.graphql.ts';

import styles from './BidPendingContent.scss';

type Props = {
    nftItem: BidPendingContent_nftItem$key;
};

export const BidPendingContent: FC<Props> = ({ nftItem: nftItemRef }) => {
    const { getAmountStrings, pendingCurrentBid, pendingTotalAmount } =
        useContext(NftPdpBidModalContext);
    const nftItem = useFragment(
        graphql`
            fragment BidPendingContent_nftItem on NftItem {
                metadata {
                    ... on NftDigitalArtMetadata {
                        name
                        media {
                            webUrl
                            posterFrameUrl
                        }
                    }
                }
            }
        `,
        nftItemRef
    );
    const { metadata } = nftItem || {};
    const { name, media } = metadata || {};
    const { webUrl, posterFrameUrl } = media || {};
    const { etherWithConversion } = getAmountStrings(
        pendingCurrentBid ? utils.formatEther(pendingCurrentBid) : ''
    );
    const { etherWithConversion: actualBidEtherWithConversion } = getAmountStrings(
        pendingTotalAmount ? utils.formatEther(pendingTotalAmount) : ''
    );
    const hasBuyerPremium = pendingTotalAmount !== pendingCurrentBid;
    return (
        <HeadingLevel>
            {Header => (
                <div className={styles.bidPendingWrapper}>
                    <div className={styles.headerWrapper}>
                        <Spinner size="tiny" black containerClass={styles.spinner} />
                        <Header className={styles.header}>
                            <FormattedMessage
                                defaultMessage="Your Bid is Pending"
                                id="nft.pdp.bidPendingModal.header"
                            />
                        </Header>
                    </div>
                    <div className={styles.itemDetails}>
                        <img
                            className={styles.itemImage}
                            src={addParams(posterFrameUrl || webUrl, styles.MEDIA_WIDTH) || ''}
                            alt=""
                        />
                        <div className={styles.itemDetailsText}>
                            <strong>{name}</strong>
                            <div>
                                <FormattedMessage
                                    defaultMessage="Your Bid: {bidValue}"
                                    id="nft.pdp.bidPendingModal.yourBid"
                                    values={{
                                        bidValue: etherWithConversion,
                                    }}
                                />
                            </div>
                            {hasBuyerPremium && (
                                <div>
                                    <FormattedMessage
                                        defaultMessage="Total Amount: {bidValue}"
                                        id="nft.pdp.bidPendingModal.totalAmount"
                                        values={{
                                            bidValue: actualBidEtherWithConversion,
                                        }}
                                    />
                                </div>
                            )}
                        </div>
                    </div>
                    <ModalBody className={styles.pendingModalBody}>
                        <Header className={styles.pendingBodyHeader}>
                            <FormattedMessage
                                defaultMessage="What happens next?"
                                id="nft.pdp.bidPendingModal.whatHappensNext"
                            />
                        </Header>
                        <OrderedList className={styles.list}>
                            <li className={styles.listItem}>
                                {hasBuyerPremium ? (
                                    <FormattedMessage
                                        defaultMessage="The total amount plus the gas fee will be withdrawn from your connected wallet."
                                        id="nft.pdp.bidPendingModal.copyBp1"
                                    />
                                ) : (
                                    <FormattedMessage
                                        defaultMessage="The bid amount plus the gas fee will be withdrawn from your connected wallet."
                                        id="nft.pdp.bidPendingModal.copy1"
                                    />
                                )}
                            </li>
                            <li className={styles.listItem}>
                                <FormattedMessage
                                    defaultMessage="If you are outbid, you may place another bid on this item."
                                    id="nft.pdp.bidPendingModal.copy2"
                                />
                            </li>
                            <li className={styles.listItem}>
                                <FormattedMessage
                                    defaultMessage="If you are the highest bidder at the end of the auction, you will become the owner of this item."
                                    id="nft.pdp.bidPendingModal.copy3"
                                />
                            </li>
                        </OrderedList>
                    </ModalBody>
                </div>
            )}
        </HeadingLevel>
    );
};
